import { Form, InputNumber, Select } from "antd"
import type { FormItemProps } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { CUSTOM, DURATION_PART_MAX } from "../../helpers/duration-presets"
import type { AutomationFormValues } from "../../helpers/form-values"

type PartName =
  | "delay_weeks"
  | "delay_days"
  | "delay_hours"
  | "delay_minutes"
  | "cadence_weeks"
  | "cadence_days"
  | "cadence_hours"

const partMax = (name: PartName) =>
  DURATION_PART_MAX[
    name.slice(name.indexOf("_") + 1) as keyof typeof DURATION_PART_MAX
  ]

interface DurationPart {
  name: PartName
  label: string
  unit: string
}

interface DurationControlProps {
  id: string
  label: string
  extra?: ReactNode
  presetName: "delay_preset" | "cadence_preset"
  presets: { value: string; label: string }[]
  parts: DurationPart[]
  /** Checks the whole duration; attached to the last part, re-run when any part changes. */
  totalRule: FormItemProps["rules"]
}

// A preset Select plus "Custom", which reveals the exact parts. The parts are
// noStyle items, so their errors (and a server error on the preset) show
// under this control's own label.
export function DurationControl({
  id,
  label,
  extra,
  presetName,
  presets,
  parts,
  totalRule
}: DurationControlProps) {
  const { t } = useTranslation()
  const custom = Form.useWatch(presetName) === CUSTOM
  const partNames = parts.map((part) => part.name)
  // The outer item has no name, so AntD does not tie its helper to the select.
  const extraId = `${id}-extra`

  return (
    <Form.Item
      label={label}
      htmlFor={id}
      extra={extra && <span id={extraId}>{extra}</span>}
      required
    >
      <div className={"flex flex-col gap-2"}>
        <Form.Item<AutomationFormValues> name={presetName} noStyle>
          <Select
            id={id}
            aria-describedby={extra ? extraId : undefined}
            className={"w-full sm:w-56"}
            options={[
              ...presets,
              { value: CUSTOM, label: t("settingsAutomation.duration.custom") }
            ]}
          />
        </Form.Item>
        {custom && (
          <div
            role={"group"}
            aria-label={t("settingsAutomation.duration.customGroup", { label })}
            className={"flex flex-wrap items-center gap-2"}
          >
            {parts.map((part, index) => {
              const last = index === parts.length - 1
              return (
                <span
                  key={part.name}
                  className={"inline-flex items-center gap-1"}
                >
                  <Form.Item<AutomationFormValues>
                    name={part.name}
                    noStyle
                    dependencies={last ? partNames.slice(0, -1) : undefined}
                    rules={last ? totalRule : undefined}
                  >
                    <InputNumber
                      aria-label={part.label}
                      min={0}
                      max={partMax(part.name)}
                      precision={0}
                      className={"w-20"}
                    />
                  </Form.Item>
                  <span aria-hidden={"true"}>{part.unit}</span>
                </span>
              )
            })}
          </div>
        )}
      </div>
    </Form.Item>
  )
}
